<template>
	<footer class="c-generations-footer" v-if="generation.status === 'DONE'">
		<button @click="copyJson" type="button" class="btn btn-box btn-icon">
			<svg
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M10 20L14 4M18 8.00004L21.2929 11.2929C21.6834 11.6835 21.6834 12.3166 21.2929 12.7071L18 16M6 16L2.70711 12.7071C2.31658 12.3166 2.31658 11.6835 2.70711 11.2929L6 8.00004"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>

			<span> Copy JSON </span>
		</button>
		<button @click="copyMarkdown" type="button" class="btn btn-box btn-icon">
			<svg
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M16 10V14M16 14L17.5 12.75M16 14L14.5 12.75M7 14V10L9.25 12L11.5 10V14M4 5H20C20.5523 5 21 5.44772 21 6V18C21 18.5523 20.5523 19 20 19H4C3.44772 19 3 18.5523 3 18V6C3 5.44772 3.44772 5 4 5Z"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>

			<span> Copy Markdown </span>
		</button>
		<article v-if="generationType && generationType.tips">
			<ul>
				<li v-for="(tip, i) in generationType.tips" :key="i">
					<svg
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M7 13L10 16L17 8"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
					<p>
						{{ tip }}
					</p>
				</li>
			</ul>
		</article>
	</footer>
</template>

<script>
import generationTypes from '@swipekit/lib/generation-types';

export default {
	props: {
		generation: {},
	},

	computed: {
		generationType: function () {
			let type = this.generation.type;

			let generationType = null;

			for (let i = 0; i < generationTypes.length; i++) {
				let s = generationTypes[i];
				if (type === s.value) {
					generationType = s;
					break;
				}
			}

			return generationType;
		},
	},

	methods: {
		copyJson: function () {
			let str = JSON.stringify(this.generation.result, null, 2);
			navigator.clipboard.writeText(str);
			this.$store.dispatch('message/sendMessage', {
				message: 'JSON copied!',
			});
		},
		copyMarkdown: function () {
			let str = '';
			if (this.generation.type === 'VIDEOTOSTORYBOARD') {
				str = this.videotostoryboardToMarkdown(this.generation);
			}
			if (this.generation.type === 'CONCEPT') {
				str = this.conceptToMarkdown(this.generation);
			}
			if (this.generation.type === 'COPY') {
				str = this.generation.result.copy;
			}
			navigator.clipboard.writeText(str);
			this.$store.dispatch('message/sendMessage', {
				message: 'Markdown copied!',
			});
		},
		videotostoryboardToMarkdown: function (generation) {
			if (!generation.result) {
				return '';
			}
			let scenes = generation.result;

			let markdown = '';

			scenes.forEach((scene, index) => {
				// Scene header
				markdown += `Scene ${index + 1}: ${scene.type}\n`;

				// Description
				if (scene.description) {
					markdown += `Description: ${scene.description}\n`;
				}

				// Details
				if (
					scene.details &&
					Array.isArray(scene.details) &&
					scene.details.length > 0
				) {
					markdown += `Details:\n`;
					scene.details.forEach((detail) => {
						markdown += `- ${detail}\n`;
					});
				}

				// Add an empty line between scenes
				markdown += `\n`;
			});

			return markdown.trim();
		},
		conceptToMarkdown(generation) {
			if (!generation.result) {
				return '';
			}
			let data = generation.result;
			let md = '';

			// Helper to format arrays
			const formatArray = (arr) => arr.map((item) => `- ${item}`).join('\n');

			// Convert main concept
			if (data.concept) {
				const c = data.concept;
				md += `## Concept: ${c.concept_name}\n\n`;
				if (c.angle) md += `**Angle:** ${c.angle}\n\n`;
				if (c.solved_problem)
					md += `**Solved Problem:** ${c.solved_problem}\n\n`;
				if (c.core_desire)
					md += `**Core Desires:**\n${formatArray(c.core_desire)}\n\n`;
				if (c.emotion_to_evoke)
					md += `**Emotions to Evoke:**\n${formatArray(
						c.emotion_to_evoke
					)}\n\n`;
			}

			// Convert hooks
			if (data.hooks && Array.isArray(data.hooks)) {
				md += `## Hooks\n\n`;
				data.hooks.forEach((hook, index) => {
					md += `### Hook ${index + 1}\n\n`;
					if (hook.central_idea) md += `**Idea:** ${hook.central_idea}\n\n`;
					if (hook.text_overlay)
						md += `**Text overlay:** ${hook.text_overlay}\n\n`;
					if (hook.visual_hook)
						md += `**Visual hook:** ${hook.visual_hook}\n\n`;
					if (hook.voice_over) md += `**Voice over:** ${hook.voice_over}\n\n`;
				});
			}

			return md.trim();
		},
	},
};
</script>

<style lang="scss">
.c-generations-footer {
	padding: 1rem;
	.btn {
		font-size: var(--font-size-sm);
		margin-right: var(--margin);
	}

	article {
		margin-top: 1rem;
		padding: 0.5rem;
		border-radius: 0.25rem;
		background-color: hsl(var(--hue-success), 48%, 94%);

		strong {
			display: block;
			font-weight: 500;
			margin-bottom: 0.5rem;
		}

		ul {
			list-style: none;
			margin: 0;
			padding: 0;

			li {
				display: grid;
				grid-template-columns: 24px 1fr;
				grid-column-gap: var(--margin);

				&:last-of-type {
					p {
						margin-bottom: 0;
					}
				}
			}

			p {
				font-size: var(--font-size-sm);

				&:last-of-type {
					margin-bottom: 0;
				}
			}

			svg {
				color: hsl(var(--hue-success), 50%, 58%);
			}
		}
	}
}
</style>
